// Fire Safety — fire safety measures + FRA tracker per building (Phase 6)
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flame } from "lucide-react";
import RegisterScaffold from "@/components/RegisterScaffold";
import FireChecksTab, { useFireCheckEligibleClients } from "@/components/buildingsafety/FireChecksTab";
import Pill from "@/components/Pill";

const BRAND = "var(--brand-accent, #C75A1B)";
const COND_TONE = { Satisfactory: "green", "Action required": "amber", Critical: "red", "Not assessed": "neutral" };
const FRA_TONE = { Trivial: "green", Tolerable: "green", Moderate: "amber", Substantial: "red", Intolerable: "red" };

const config = {
    title: "Fire Safety",
    sectionTag: "Safety & Compliance",
    endpoint: "/fire-safety",
    testid: "fire",
    moduleId: "fire",
    icon: Flame,
    emptyHint: "No fire safety measures recorded for these buildings yet.",
    documentLink: true,
    filters: [
        { param: "measure_type", metaKey: "measure_types", allLabel: "All measures" },
        { param: "condition", metaKey: "conditions", allLabel: "All conditions" },
    ],
    columns: [
        { key: "measure", label: "Measure", render: (r) => (<div><div className="font-semibold text-[#1F1F1F]">{r.measure}</div><div className="text-[11px]" style={{ color: "#9A968F" }}>{r.measure_type}</div></div>) },
        { key: "building_id", label: "Building", render: (r, c) => c.buildingMap[r.building_id]?.name || "—" },
        { key: "condition", label: "Condition", render: (r) => <Pill tone={COND_TONE[r.condition] || "neutral"}>{r.condition}</Pill> },
        { key: "fra_rating", label: "FRA rating", render: (r) => r.fra_rating ? <Pill tone={FRA_TONE[r.fra_rating] || "neutral"}>{r.fra_rating}</Pill> : <span className="text-[#9A968F]">—</span> },
        { key: "next_due", label: "Next due", render: (r) => <span className="font-mono text-[11.5px] text-[#2D2D2D]">{r.next_due || "—"}</span> },
        { key: "responsible", label: "Responsible", render: (r) => r.responsible || "—" },
    ],
    fields: [
        { key: "measure", label: "Measure / asset", type: "text", required: true, full: true },
        { key: "measure_type", label: "Measure type", type: "select", metaKey: "measure_types", required: true },
        { key: "condition", label: "Condition", type: "select", metaKey: "conditions", default: "Not assessed" },
        { key: "fra_rating", label: "FRA rating (if applicable)", type: "select", metaKey: "fra_ratings" },
        { key: "last_inspected", label: "Last inspected", type: "date" },
        { key: "next_due", label: "Next due", type: "date" },
        { key: "fra_date", label: "FRA date", type: "date" },
        { key: "responsible", label: "Responsible", type: "text" },
        { key: "significant_findings", label: "Significant findings", type: "textarea" },
    ],
};

export default function FireSafetyPage() {
    const weeklyEligible = useFireCheckEligibleClients("fire_check");
    const monthlyEligible = useFireCheckEligibleClients("fire_check_monthly");
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get("tab") || "register");
    const showWeekly = Array.isArray(weeklyEligible) && weeklyEligible.length > 0;
    const showMonthly = Array.isArray(monthlyEligible) && monthlyEligible.length > 0;

    if (!showWeekly && !showMonthly) return <RegisterScaffold config={config} />;

    const tabs = [["register", "Register"],
        ...(showWeekly ? [["firechecks", "Weekly Fire Checks"]] : []),
        ...(showMonthly ? [["monthlyfirechecks", "Monthly Fire Checks"]] : [])];

    return (
        <div className="space-y-5" data-testid="fire-page-wrap">
            <div className="flex items-center gap-1 border-b" style={{ borderColor: "#E5E0DA" }}>
                {tabs.map(([id, label]) => (
                    <button key={id} data-testid={`fire-tab-${id}`} onClick={() => setTab(id)}
                        className="px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors"
                        style={tab === id ? { borderColor: BRAND, color: BRAND } : { borderColor: "transparent", color: "#6F6A64" }}>
                        {label}
                    </button>
                ))}
            </div>
            {tab === "register" && <RegisterScaffold config={config} />}
            {tab === "firechecks" && <FireChecksTab eligibleClients={weeklyEligible} category="fire_check" />}
            {tab === "monthlyfirechecks" && (
                <FireChecksTab eligibleClients={monthlyEligible} category="fire_check_monthly"
                    description="Monthly fire/life-safety equipment checks (emergency lighting, cupboards, roof & external lighting, car park & EV points, first aid/AED kits etc.) — select the building and the right monthly check template."
                    newButtonLabel="New monthly check" emptyLabel="monthly fire checks" />
            )}
        </div>
    );
}
